import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PENDING_CANCELLATION, NONE, CANCELLED, TRIAL_EXPIRED, PAYMENT_ISSUE } from '../constants/account-issues';
import { TRIAL_PLAN_CODE } from '../../../../constants/plan-codes';
import { BRIGHTBACK_CANCELLATION_QUERY_STRING } from '../sections/cancellation/brightback/hooks/use-get-brightback-data';

import Global from '../../../../store/global-selectors';
import { isTrialPlan } from './is-trial-plan';

const getIsEndDateInFuture = (endDateTimeUTC) => {
  const currentDate = new Date();
  return currentDate <= new Date(endDateTimeUTC);
};
const shouldOptimisticallyShowCancellationBlock = (searchParams) => {
  return searchParams === BRIGHTBACK_CANCELLATION_QUERY_STRING;
};

export const usePlanInfoDerivedSubscriptionInfo = () => {
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  return useDeriveSubscriptionInfo(planInfo);
};

export const useDeriveSubscriptionInfo = (planInfo) => {
  const {
    isTrialExpired,
    billingCycleEndDateTimeUTC,
    isSubscriptionExpired,
    isExpired,
    isPlanLocked,
    isLastInvoicePastDueOrFailed,
    plan,
    isCanceled,
    planCode,
    billingPlanId,
  } = planInfo;
  const { search: searchParams } = useLocation();
  const expiredMerged = isExpired || isSubscriptionExpired;
  const showCancelBlock = shouldOptimisticallyShowCancellationBlock(searchParams);
  const didSellerCancelViaBrightback = !!searchParams && showCancelBlock;
  const isTrialCode = plan?.code === TRIAL_PLAN_CODE || planCode === TRIAL_PLAN_CODE || isTrialPlan({ billingPlanId });
  const planWillExpire = !expiredMerged && (didSellerCancelViaBrightback || isCanceled) && getIsEndDateInFuture(billingCycleEndDateTimeUTC);
  const trialCanceledButNotYetExpired = isTrialCode && !isTrialExpired && (didSellerCancelViaBrightback || isCanceled);
  const hasActivePaidPlan = !isTrialCode && !expiredMerged;
  const inactiveAccount = expiredMerged && !planWillExpire && !isTrialCode;
  const canReactivatePaidPlan = !isPlanLocked && planWillExpire;
  const canReactivateTrial = !isPlanLocked && trialCanceledButNotYetExpired;
  const isSellerIsPastDueOnBalanceAndNotInTrial = isLastInvoicePastDueOrFailed && !isTrialExpired;
  const activeTrialAccount = isTrialCode && !isTrialExpired && !expiredMerged && !planWillExpire;
  const expiredTrialAccount = isTrialCode && isTrialExpired;
  const isTrialAccount = activeTrialAccount || expiredTrialAccount || trialCanceledButNotYetExpired;
  const isCTPPlan = !(isTrialAccount && !hasActivePaidPlan) && isPlanLocked;

  const getAccountIssueStatus = () => {
    if (planWillExpire) {
      return PENDING_CANCELLATION;
    }
    if (isTrialExpired) {
      return TRIAL_EXPIRED;
    }
    if (inactiveAccount) {
      return CANCELLED;
    }
    if (isSellerIsPastDueOnBalanceAndNotInTrial) {
      return PAYMENT_ISSUE;
    }
    return NONE;
  };
  const accountIssueStatus = getAccountIssueStatus();
  return {
    isTrialAccount,
    hasActivePaidPlan,
    inactiveAccount,
    canReactivatePaidPlan,
    canReactivateTrial,
    activeTrialAccount,
    expiredTrialAccount,
    planWillExpire,
    accountIssueStatus,
    isCTPPlan,
  };
};
